import type { Ability } from '@core/abilities-types';
import type { Company } from '@core/company-types';
import type { FeatureDiscoveryFeatures } from '@core/feature-discovery-types';
import type { Membership } from '@core/memberships-types';
import type { OnboardingVideosWatched } from '@core/onboarding-lib-types';
import type { WeekStartDay } from '@core/time-legacy';
import type { BadgeColor, IconName } from '@core/user-interface-legacy';

import type { AdminReportInterval } from './account.enums';

export interface UserBase {
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  photoNormalUrl: string;
  photoThumbUrl: string;
  timeFormat24: boolean;
  timezone: string;
  weekStartDay: WeekStartDay;
  hasAvatar: boolean;
  initials: string;
  phone: string;
  anonymizedAt: string;
  membershipIsActive?: boolean;
}

export interface UserRole {
  membership?: Membership;
  roleAbilities: Ability[];
  roleAdmin: boolean;
  roleId: number;
  roleName: string;
}

export interface UserWithRole extends UserBase, UserRole {}

export interface UserReference extends User {
  type: 'Admin';
}

export interface User extends UserBase, UserRole {
  offerIds: number[] | null;
  talentPoolIds: number[] | null;
  viewAllJobs: boolean;
  requisitionIds: number[] | null;
  viewAllRequisitions: boolean;
  accessiblePlacementIds: number[] | null;

  hasCandidateAccess?: boolean;
}

export interface CurrentUser extends UserBase, UserRole {
  allCompaniesCount: number;
  authenticationToken: string;
  calendarSyncToken: string;
  companies: Company[];
  companyId: number | null;
  featureDiscovery: FeatureDiscoveryFeatures;
  onboardingVideos: OnboardingVideosWatched;
  phone: string;
  preferredLanguage: string;
  socketToken: string;
  unreadProductUpdates: boolean;
}

export type AdminReportConfiguration = {
  time: string;
  interval: AdminReportInterval;
  day: number;
};

export type DeletionRequestsData = {
  companiesWithNoOtherAdmins: Array<{ id: number; name: string }>;
  message?: string;
};

export enum AdditionalUserSelectionOptionKey {
  AdminIdsAddAdminPerformingAction = 'adminIdsAddAdminPerformingAction',
  AddAdminPerformingAction = 'addAdminPerformingAction',
  AdminIdsAddHiringManager = 'adminIdsAddHiringManager',
  AddHiringManager = 'addHiringManager',
  AdminIdsAddRecruiter = 'adminIdsAddRecruiter',
  AddRecruiter = 'addRecruiter',
  AddInterviewers = 'addInterviewers',
  AdminIdsAddInterviewers = 'adminIdsAddInterviewers',
}

export type AdditionalUserSelectionOption = {
  key: AdditionalUserSelectionOptionKey;
  heading: string;
  subheading: string;
  tooltip: string;
  isSelected: boolean;
  disabled?: boolean;
  badge?: {
    icon: IconName;
    color: BadgeColor;
    label: string;
  };
};
